import React, { useState, useEffect } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { Tabs, Row, Col, TabsProps, Statistic } from 'antd'
import { useIntl } from 'react-intl'
import { useManualQuery } from 'graphql-hooks'
import { getTeamById, getUsersFilteredByStatus } from '../../../../graphql/custom-queries'
import * as logger from '../../../../services/logger'

import IntlMessages from '../../../../util/IntlMessages'
import { FrontendUrls } from '../../../../types/urls'
import { PageHeader } from '../../../../design-system/PageHeader'
import { redirectIfEntityNotFound } from '../../../../util/entity-not-found-redirect'
import CircularProgress from '../../../../components/circular-progress'
import { UserAvatar } from '@vacationtracker/shared/components/user-avatar'
import { useLocationAdmin } from '../../../../hooks/use-location-admin'

import { IGetTeamByIdData, IGetTeamById, ITeamUsers, IGetTeamByIdApprovers } from '../../../../types/teams'
import { IGetUsersFilteredByStatusResponse } from '../../../../types/custom-queries'
import { ApproversLevelEnum } from '@vacationtracker/shared/types/team'

interface ITeamsPage {
  match: {
    params: {
      id: string
    }
  }
}

const TeamsPage = ({ match }: ITeamsPage): React.ReactElement => {
  const { formatMessage } = useIntl()
  const history = useHistory()
  const [isLoading, setIsLoading] = useState(true)
  const [team, setTeam] = useState<IGetTeamById>({
    id: '',
    name: '',
    timestamp: '',
    users: [],
    approvers: [],
    isDefault: false,
  })
  const [approvalLevel, setApprovalLevel] = useState<string>(ApproversLevelEnum.FIRST)
  const [managedUserIds, setManagedUserIds] = useState<Set<string>>(new Set())

  const { isLocationAdmin } = useLocationAdmin()
  const [getTeamByIdQuery] = useManualQuery<IGetTeamByIdData, { id: string }>(getTeamById)
  const [usersFilteredQuery] = useManualQuery<IGetUsersFilteredByStatusResponse, { status: string }>(getUsersFilteredByStatus)

  useEffect(() => {
    fetchTeam(match.params.id)
  }, [match.params.id])

  const fetchTeam = async (id) => {
    try {
      const response = await getTeamByIdQuery({ variables: { id } })
      if (!response.data || response.error) throw response.error
      if (!redirectIfEntityNotFound(response.data.getTeam, FrontendUrls.departments, history)) return

      setApprovalLevel(
        response.data?.getTeam.approvers.find((approver) => approver.approverLevels?.includes(ApproversLevelEnum.SECOND)) ? ApproversLevelEnum.SECOND : ApproversLevelEnum.FIRST
      )

      setTeam(response.data?.getTeam)

      setIsLoading(false)

      if (isLocationAdmin) {
        const usersResponse = await usersFilteredQuery({ variables: { status: 'ACTIVE' } })
        if (usersResponse.data?.getUsersFiltered?.users) {
          setManagedUserIds(new Set(usersResponse.data.getUsersFiltered.users.map((u) => u.id)))
        }
      }
    } catch (err) {
      logger.error('error fetching team', err)
    }
  }

  const canLinkToUser = (userId: string) => !isLocationAdmin || managedUserIds.has(userId)

  const operations = (
    <Link to={`/app/settings/departments/${team.id}/edit`}>
      <IntlMessages id="teams.editTeam" />
    </Link>
  )

  const teamsTabs: TabsProps['items'] = [
    {
      key: 'general',
      label: <IntlMessages id="app.general" />,
      children: (
        <div className="view-form">
          <Row align="top" gutter={[0, 20]}>
            <Col xl={18} lg={24}>
              <Statistic title={<IntlMessages id="app.name" />} value={team.name} />
            </Col>

            <Col xl={18} lg={24}>
              <Statistic
                title={<IntlMessages id="app.role.approvers" />}
                value={`${team.approvers.length} ${formatMessage({ id: team.approvers.length === 1 ? 'app.role.approver' : 'app.role.approvers' })}`}
              />
              {approvalLevel === ApproversLevelEnum.FIRST ? (
                <Row gutter={10} style={{ marginTop: 10 }}>
                  {team.approvers.map((approvers: IGetTeamByIdApprovers) => {
                    return (
                      <Col key={approvers.id} xs={24} sm={12} md={12} lg={8} xl={8} className="user-avatar">
                        <UserAvatar
                          id={approvers.id}
                          avatar={approvers.imageUrl}
                          name={approvers.name}
                          isShowingName={true}
                          isLinkToPage={canLinkToUser(approvers.id)}
                          greyOut={!canLinkToUser(approvers.id)}
                          shape="circle"
                        />
                      </Col>
                    )
                  })}
                </Row>
              ) : (
                <>
                  <strong style={{ marginTop: 20, display: 'flex' }}>
                    <IntlMessages id="app.role.approversLevelFrist" />
                  </strong>
                  <Row gutter={10} style={{ marginTop: 10 }}>
                    {team.approvers.map((approvers: IGetTeamByIdApprovers) => {
                      if (approvers.approverLevels?.includes(ApproversLevelEnum.FIRST)) {
                        return (
                          <Col key={approvers.id} xs={24} sm={12} md={12} lg={8} xl={8} className="user-avatar">
                            <UserAvatar
                              id={approvers.id}
                              avatar={approvers.imageUrl}
                              name={approvers.name}
                              isShowingName={true}
                              isLinkToPage={canLinkToUser(approvers.id)}
                              greyOut={!canLinkToUser(approvers.id)}
                              shape="circle"
                            />
                          </Col>
                        )
                      }
                    })}
                  </Row>
                  <strong style={{ marginTop: 30, display: 'flex' }}>
                    <IntlMessages id="app.role.approversLevelSecound" />
                  </strong>
                  <Row gutter={10} style={{ marginTop: 10 }}>
                    {team.approvers.map((approvers: IGetTeamByIdApprovers) => {
                      if (approvers.approverLevels?.includes(ApproversLevelEnum.SECOND)) {
                        return (
                          <Col key={approvers.id} xs={24} sm={12} md={12} lg={8} xl={8} className="user-avatar">
                            <UserAvatar
                              id={approvers.id}
                              avatar={approvers.imageUrl}
                              name={approvers.name}
                              isShowingName={true}
                              isLinkToPage={canLinkToUser(approvers.id)}
                              greyOut={!canLinkToUser(approvers.id)}
                              shape="circle"
                            />
                          </Col>
                        )
                      }
                    })}
                  </Row>
                </>
              )}
            </Col>

            <Col xl={18} lg={24}>
              <Statistic title={<IntlMessages id="app.users" />} value={`${team.users.length} ${formatMessage({ id: team.users.length === 1 ? 'app.user' : 'app.users' })}`} />
              {team.users.length > 0 ? (
                <Row gutter={10} style={{ marginTop: 10 }}>
                  {team.users.map((user: ITeamUsers) => {
                    return (
                      <Col key={user.id} xs={24} sm={12} md={12} lg={8} xl={8} style={{ marginBottom: 14 }}>
                        <UserAvatar
                          id={user.id}
                          avatar={user.imageUrl}
                          name={user.name}
                          isShowingName={true}
                          isLinkToPage={canLinkToUser(user.id)}
                          greyOut={!canLinkToUser(user.id)}
                          shape="circle"
                        />
                      </Col>
                    )
                  })}
                </Row>
              ) : (
                <IntlMessages id="team.hasNoUsers" />
              )}
            </Col>
          </Row>
        </div>
      ),
    },
  ]

  return (
    <div className="main-content">
      {isLoading ? (
        <CircularProgress />
      ) : (
        <>
          <PageHeader title={team?.name} parent={[{ label: <IntlMessages id="app.departments" />, to: FrontendUrls.departments }]} />
          <div className="main-content-body">
            <div className="team-body">
              <Tabs defaultActiveKey="general" tabBarExtraContent={operations} items={teamsTabs} />
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default TeamsPage
